import React, { useState, useEffect } from "react";
import moment from "moment-timezone";
import { IEvent, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import Button from "../Button/Button";
import Dropdown, { IOption } from "../Dropdown/Dropdown";
import AkcObedienceAwards from "./AkcObedienceAwards/AkcObedienceAwards";
import AkcRallyObedienceAwards from "./AkcRallyObedienceAwards/AkcRallyObedienceAwards";
import AkcScentworkAwards from "./AkcScentworkAwards/AkcScentworkAwards";
import UkcObedienceAwards from "./UkcObedienceAwards/UkcObedienceAwards";
import UkcRallyObedienceAwards from "./UkcRallyObedienceAwards/UkcRallyObedienceAwards";
import UkcAgilityAwards from "./UkcAgilityAwards/UkcAgilityAwards";
import { apiService } from "../../services/api.service";
import { toast } from "../Toast/ToastManager";

import "./ShowAwardsModal.scss";

interface IProps {
  event: IEvent;
  show: IShow;
  onClose: () => void;
}

const ShowAwardsModal: React.FC<IProps> = ({ event, show, onClose }) => {
  const [runOffs, setRunOffs] = useState<any[]>([]);
  const [editingRunOff, setEditingRunOff] = useState<number | null>(null);
  const [orderedPlacements, setOrderedPlacements] = useState<Array<{key: string; arm_number: string; score_id?: string; placement: number}>>([]);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const loadRunOffs = async () => {
    try {
      const response = await apiService.getRunOffs(event._id, show._id);
      setRunOffs(response.run_offs || []);
      setRefreshKey(prev => prev + 1); // Force awards component to refresh
    } catch (error) {
      console.error("Failed to load run-offs:", error);
    }
  };

  useEffect(() => {
    // Load run-offs if this is an Obedience show (AKC or UKC)
    if ((event.sanctioning_club === Constants.sanctioning_club.AKC || event.sanctioning_club === Constants.sanctioning_club.UKC) && show.show_type === Constants.show_type.obedience) {
      loadRunOffs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event._id, show._id, event.sanctioning_club, show.show_type]);

  const handleEditRunOff = (index: number, currentWinner?: string) => {
    setEditingRunOff(index);
    
    // Initialize ordered placements from existing data if available
    const runOff = runOffs[index];
    if (runOff.ordered_placements && runOff.ordered_placements.length > 0) {
      setOrderedPlacements(runOff.ordered_placements.map((p: any, index: number) => ({
        key: p.score_id || `${p.arm_number || "no-arm"}-${index}`,
        arm_number: p.arm_number,
        score_id: p.score_id,
        placement: p.placement
      })));
    } else {
      // Initialize empty placements
      setOrderedPlacements(runOff.tied_scores.map((score: any, index: number) => ({
        key: score.score_id || `${score.arm_number || "no-arm"}-${index}`,
        arm_number: score.arm_number,
        score_id: score.score_id,
        placement: 0
      })));
    }
  };

  const handlePlacementChange = (key: string, placement: number) => {
    setOrderedPlacements(prev => 
      prev.map(p => p.key === key ? { ...p, placement } : p)
    );
  };

  const handleSaveRunOff = async (index: number) => {
    const runOff = runOffs[index];
    
    // Validate that all placements are assigned
    const hasUnassigned = orderedPlacements.some(p => p.placement === 0);
    if (hasUnassigned) {
      toast.show({
        title: "Error",
        content: "Please assign a placement to all dogs",
        type: "fail",
      });
      return;
    }

    // Validate that placements are unique and consecutive starting from 1
    const placements = orderedPlacements.map(p => p.placement).sort((a, b) => a - b);
    const expectedPlacements = Array.from({ length: placements.length }, (_, i) => i + 1);
    const isValid = placements.every((p, i) => p === expectedPlacements[i]);
    
    if (!isValid) {
      toast.show({
        title: "Error",
        content: "Placements must be consecutive starting from 1 (e.g., 1st, 2nd, 3rd)",
        type: "fail",
      });
      return;
    }

    try {
      // Build ordered_placements array with full dog info
      const orderedPlacementsWithInfo = orderedPlacements.map(op => {
        const tiedScore = runOff.tied_scores.find((ts: any, index: number) =>
          (ts.score_id && ts.score_id === op.score_id) ||
          (op.key === (ts.score_id || `${ts.arm_number || "no-arm"}-${index}`))
        );
        return {
          arm_number: op.arm_number,
          dog_name: tiedScore?.dog_name || "",
          score_id: op.score_id || tiedScore?.score_id || "",
          placement: op.placement
        };
      });

      await apiService.updateRunOffWinner(
        event._id,
        show._id,
        index,
        {
          winner_arm_number: orderedPlacementsWithInfo.find(p => p.placement === 1)?.arm_number || "",
          ordered_placements: orderedPlacementsWithInfo,
          show_element: runOff.show_element,
          level: runOff.level,
          placement: runOff.placement
        }
      );

      toast.show({
        title: "Success",
        content: "Run-off placements updated",
        type: "success",
      });

      await loadRunOffs();
      setEditingRunOff(null);
      setOrderedPlacements([]);
    } catch (error: any) {
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to update placements",
        type: "fail",
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingRunOff(null);
    setOrderedPlacements([]);
  };

  const getAwardsComponent = () => {
    // Merge the updated runOffs into the show object for the awards component
    const showWithUpdatedRunOffs = { ...show, run_offs: runOffs };

    if (event.sanctioning_club === Constants.sanctioning_club.AKC) {
      if (show.show_type === Constants.show_type.obedience) {
        return <AkcObedienceAwards event={event} show={showWithUpdatedRunOffs} key={refreshKey} />;
      }
      if (show.show_type === Constants.show_type.rally_obedience) {
        return <AkcRallyObedienceAwards event={event} show={showWithUpdatedRunOffs} key={refreshKey} />;
      }
      if (show.show_type === Constants.show_type.scentwork) {
        return <AkcScentworkAwards event={event} show={showWithUpdatedRunOffs} key={refreshKey} />;
      }
    }

    if (event.sanctioning_club === Constants.sanctioning_club.UKC) {
      if (show.show_type === Constants.show_type.obedience) {
        return <UkcObedienceAwards event={event} show={showWithUpdatedRunOffs} />;
      }
      if (show.show_type === Constants.show_type.rally_obedience) {
        return <UkcRallyObedienceAwards event={event} show={showWithUpdatedRunOffs} />;
      }
      if (show.show_type === Constants.show_type.agility) {
        return <UkcAgilityAwards event={event} show={showWithUpdatedRunOffs} />;
      }
    }

    return (
      <div className="noAwards">
        <p>Awards are not available for this event type yet.</p>
      </div>
    );
  };

  const formatShowDate = () => {
    const timezone = event.timezone || moment.tz.guess();
    return moment(show.show_date).tz(timezone).format("dddd MMMM Do, YYYY");
  };

  const formatShowName = () => {
    // Capitalize first letter of each word in show_type and combine with show_name
    const formattedType = show.show_type
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${formattedType} ${show.custom_show_name || show.show_name}`;
  };

  return (
    <div className="ShowAwardsModal">
      <div className="header">
        <h2>Awards Summary</h2>
      </div>
      <div className="showInfo">
        <div className="date">{formatShowDate()}</div>
        <div className="showName">{formatShowName()}</div>
      </div>

      <div className="content">
        {/* Run-offs Section */}
        {runOffs.length > 0 && (
          <div className="runOffsSection">
            <h3>Run-Offs</h3>
            {runOffs.map((runOff, index) => (
              <div key={index} className="runOffItem">
                <div className="runOffHeader">
                  <div className="runOffInfo">
                    <span className="level">{runOff.level}</span>
                    <span className="placement">{runOff.placement} Place</span>
                    {runOff.resolved_at && (
                      <span className="resolved">✓ Resolved</span>
                    )}
                  </div>
                </div>
                <div className="tiedDogs">
                  <strong>Tied Dogs:</strong>
                  <ul>
                    {runOff.tied_scores.map((score: any, idx: number) => (
                      <li key={idx}>
                        #{score.arm_number} - {score.dog_name}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="winnerSection">
                  {editingRunOff === index ? (
                    <div className="editForm">
                      <label>Assign Placements:</label>
                      <div className="placementsList">
                        {runOff.tied_scores.map((score: any, scoreIndex: number) => {
                          const key = score.score_id || `${score.arm_number || "no-arm"}-${scoreIndex}`;
                          const currentPlacement = orderedPlacements.find(
                            p => p.key === key
                          )?.placement || 0;
                          
                          const placementOptions: IOption[] = [
                            { id: "0", value: "Select placement..." },
                            ...runOff.tied_scores.map((_: any, idx: number) => ({
                              id: String(idx + 1),
                              value: `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} Place`
                            }))
                          ];

                          const selectedOption = placementOptions.find(opt => opt.id === String(currentPlacement)) || placementOptions[0];
                          
                          return (
                            <div key={key} className="placementRow">
                              <span className="dogInfo">
                                {score.arm_number ? `#${score.arm_number} - ` : ""}{score.dog_name}
                              </span>
                              <Dropdown
                                id={`awards-runoff-placement-${key}`}
                                value={selectedOption}
                                onChange={(e: any, value: IOption) => handlePlacementChange(key, parseInt(value.id))}
                                options={placementOptions}
                                placeholder="Select placement..."
                                label="Placement"
                                className="placementSelect"
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="editActions">
                        <Button onClick={() => handleSaveRunOff(index)}>Save Placements</Button>
                        <Button onClick={handleCancelEdit}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="winnerDisplay">
                      {runOff.ordered_placements && runOff.ordered_placements.length > 0 ? (
                        <div className="resolvedPlacements">
                          <strong>Final Placements:</strong>
                          <ul>
                            {runOff.ordered_placements
                              .sort((a: any, b: any) => a.placement - b.placement)
                              .map((p: any) => (
                                <li key={p.arm_number}>
                                  <span className="placementNumber">
                                    {p.placement}{p.placement === 1 ? 'st' : p.placement === 2 ? 'nd' : p.placement === 3 ? 'rd' : 'th'}:
                                  </span>
                                  <span className="dogDetails">
                                    #{p.arm_number} - {p.dog_name}
                                  </span>
                                </li>
                              ))}
                          </ul>
                          <Button onClick={() => handleEditRunOff(index, runOff.winner_arm_number)}>
                            Edit Placements
                          </Button>
                        </div>
                      ) : runOff.winner_arm_number ? (
                        <>
                          <strong>Winner:</strong> #{runOff.winner_arm_number}
                          <Button onClick={() => handleEditRunOff(index, runOff.winner_arm_number)}>
                            Edit
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => handleEditRunOff(index)}>
                          Enter Placements
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {getAwardsComponent()}
      </div>
      <div className="actions">
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
};

export default ShowAwardsModal;